import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, OnInit } from '@angular/core';
import { MatLegacyCheckboxChange as MatCheckboxChange } from '@angular/material/legacy-checkbox';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { Router } from '@angular/router';
import { UserPermissionsService } from '@app/core/user-permissions/user-permissions.service';
import { NotificationType } from '@app/shared/interfaces';
import { ApiAccessToken, ApiAccessTokenScope, AppsService } from '@app/shared/services/apps/apps.service';
import { IntercomService } from '@app/shared/services/intercom.service';
import { ToastNotificationsService } from '@app/shared/services/toast-notifications/toast-notifications.service';
import { DialogDeleteAPIAccessTokenComponent } from '../../components/DialogDeleteAPIAccessToken/DialogDeleteAPIAccessToken.component';
import { DialogRevealApiAccessTokenComponent } from '../../components/DialogRevealApiAccessToken/DialogRevealApiAccessToken.component';

@Component({
  standalone: false,
  selector: 'sbnb-api-keys',
  templateUrl: './api-keys.component.html',
  styleUrls: ['./api-keys.component.scss'],
  animations: [trigger('fadeInOut', [state('void', style({ opacity: 0 })), transition('void <=> *', [animate(100)])])],
})
export class ApiKeysComponent implements OnInit {
  loading = false;
  saveStatus = 0;
  accessTokens: ApiAccessToken[] = [];
  loadingTokens = false;
  creatingToken = false;
  ApiAccessTokenScope = ApiAccessTokenScope;

  constructor(
    private appsService: AppsService,
    private permissions: UserPermissionsService,
    private dialog: MatDialog,
    private readonly router: Router,
    private toast: ToastNotificationsService,
    private readonly intercomService: IntercomService
  ) {}

  ngOnInit() {
    this.preventSecondaryUsers().then(() => {
      this.loadingTokens = true;
      this.appsService.getAPIAccessTokens().subscribe((res) => {
        this.accessTokens = res;
        this.loadingTokens = false;
      });
    });
  }

  async preventSecondaryUsers() {
    this.permissions.loaded.subscribe((loaded) => {
      if (loaded) {
        this.permissions.isPrimaryUser().subscribe((isPrimaryUser) => {
          if (!isPrimaryUser) {
            this.toast.open('You do not have access to this page', 'Dismiss', NotificationType.Error);
            this.router.navigate(['/dashboard']);
          }
        });
      }
    });
  }

  updateAccessToken(token: ApiAccessToken) {
    this.saveStatus = 1;
    this.appsService.updateAPIAccessToken(token.id, token).subscribe(() => {
      this.saveStatus = 2;
    });
  }

  deleteAccessTokenPrompt(token: ApiAccessToken) {
    const dialogRef = this.dialog.open(DialogDeleteAPIAccessTokenComponent, {
      width: '720px',
      data: {
        token,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.accessTokens.splice(
          this.accessTokens.findIndex((t) => t.id === token.id),
          1
        );
      }
    });
  }

  tokenCopied(token: ApiAccessToken) {
    token.recently_copied = true;

    setTimeout(() => {
      token.recently_copied = false;
    }, 1000);
  }

  addAccessToken() {
    this.creatingToken = true;

    const payload = {
      name: 'new-access-token',
      scopes: [ApiAccessTokenScope.Read, ApiAccessTokenScope.Write],
    };

    this.appsService.createAPIAccessToken(payload).subscribe(
      (res) => {
        this.creatingToken = false;

        if (res.id) {
          this.accessTokens.unshift(res);
        }
      },
      () => {
        this.creatingToken = false;
      }
    );
  }

  revealToken(id: ApiAccessToken['id']) {
    const dialogRef = this.dialog.open(DialogRevealApiAccessTokenComponent, {
      width: '720px',
      data: {
        id,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.secret) {
        const index = this.accessTokens.findIndex((token) => token.id === id);

        if (index !== -1) {
          this.accessTokens[index].secret = result.secret;
        }
      }
    });
  }

  updateAccessTokenScope(token: ApiAccessToken, scope: ApiAccessTokenScope, event: MatCheckboxChange) {
    if (event.checked) {
      token.scopes.push(scope);
    } else {
      const index = token.scopes.findIndex((scopeItem) => scopeItem === scope);

      if (index > -1) {
        token.scopes.splice(index, 1);
      }
    }

    this.updateAccessToken(token);
  }

  openIntercomHelp(articleId: string) {
    this.intercomService.openHelpArticle(articleId);
  }
}
